import React, { useMemo } from 'react'
import { Container } from '../Container'
import { CircularProgress, Grid, Theme, Typography } from '@material-ui/core'
import { TemplateServiceChartRow, TemplateServiceDetailRow } from './Components'
import { ServiceDetailHeader } from './Components/Header'
import { useCalendarGrid } from './hooks'
import { DAY_CELL_W, HEADER_H, VENDOR_BAR_H } from './constants'
import { CalendarGrid } from './Components/CalendarGrid'
import {
    AreaConfig,
    AreaStatusTag,
    Folder,
    ListVendor,
    ModelMap,
    Service,
    TemplateWorkorder,
    UnitConfig,
    ezScheduleDetails,
} from '../../models'
import { DateTime } from 'luxon'
import { useTimezone } from '../../contexts/timezone'
import {
    UpdateEzScheduleParams,
    UpdateTemplateServiceVendorParams,
} from '../../hooks/useEzSchedule/types'
import { useTemplateScheduleGanttStyles } from './styles'

interface Props {
    theme: Theme
    serviceList: Service[]
    vendorList: ListVendor[]
    infrastructure: Folder
    unitConfigMap: ModelMap<UnitConfig>
    areaConfigMap: ModelMap<AreaConfig>
    areaStatusTagMap: ModelMap<AreaStatusTag>
    ezSchedule: ezScheduleDetails
    ezScheduleLoading: boolean
    templateWorkorders?: TemplateWorkorder[]
    selectedPhaseId: number
    updateEzSchedule: (params: UpdateEzScheduleParams) => void
    updateTemplateServiceVendor: (
        params: UpdateTemplateServiceVendorParams,
    ) => void
}

export const TemplateScheduleGantt = (props: Props) => {
    const {
        theme,
        infrastructure,
        ezSchedule,
        ezScheduleLoading,
        templateWorkorders = [],
        updateEzSchedule,
        updateTemplateServiceVendor,
        selectedPhaseId,
    } = props

    const timezoneState = useTimezone()
    const classes = useTemplateScheduleGanttStyles()

    const selectedPhase = ezSchedule?.phases.find(
        (phase) => phase.phase?.id === selectedPhaseId,
    )
    const templateUnits = selectedPhase?.template_units ?? []
    const templateServices = selectedPhase?.template_services ?? []

    const startDate = ezSchedule?.work_start_date
        ? DateTime.fromISO(ezSchedule.work_start_date).setZone(
              timezoneState.currentTimezone,
          )
        : DateTime.now()
    const endDate = ezSchedule?.work_end_date
        ? DateTime.fromISO(ezSchedule.work_end_date).setZone(
              timezoneState.currentTimezone,
          )
        : DateTime.now()
    const displayEndDate = endDate.plus({ days: 1 })

    const grid = useCalendarGrid(startDate, displayEndDate)

    const workordersByVendorDate = useMemo(() => {
        const map = new Map<string, TemplateWorkorder[]>()
        for (const wo of templateWorkorders) {
            const woDate = DateTime.fromISO(wo.start_date)
                .setZone(timezoneState.currentTimezone)
                .toISODate()
            if (!woDate) continue
            const key = `${wo.template_service_vendor}-${woDate}`
            const arr = map.get(key)
            if (arr) {
                arr.push(wo)
            } else {
                map.set(key, [wo])
            }
        }
        return map
    }, [templateWorkorders, timezoneState.currentTimezone])

    const workOrderCountByService = useMemo(() => {
        const list: number[] = []
        const two = templateWorkorders ?? []
        templateServices.forEach((ts) => {
            const count = two.filter((wo) =>
                ts.template_service_vendors.some(
                    (tsv) => tsv.id === wo.template_service_vendor,
                ),
            ).length
            list.push(count)
        })
        return list
    }, [selectedPhaseId, templateServices, templateWorkorders])

    if (ezScheduleLoading) {
        return <CircularProgress />
    }

    if (!ezSchedule) {
        return <div>No schedule found</div>
    }

    if (!ezSchedule.work_start_date || !ezSchedule.work_end_date) {
        return <div>No work start or end date found</div>
    }

    const handleChooseStartTransferDate = (clickX: number) => {
        if (ezSchedule === null || grid.gridRef.current === null) return

        const gridPos = grid.getClickedGridPosition(clickX)
        const clickedDate = grid.convertGridPositionToDate(gridPos)
        updateEzSchedule({
            transfer_start_date: clickedDate.toISO(),
        })
    }

    const handleChooseEndTransferDate = (clickX: number) => {
        if (ezSchedule === null || grid.gridRef.current === null) return

        const gridPos = grid.getClickedGridPosition(clickX)
        const clickedDate = grid.convertGridPositionToDate(gridPos)
        updateEzSchedule({
            transfer_end_date: clickedDate.toISO(),
        })
    }

    const handleChooseDestUnitReadyDate = (clickX: number) => {
        if (ezSchedule === null || grid.gridRef.current === null) return

        const gridPos = grid.getClickedGridPosition(clickX)
        const clickedDate = grid.convertGridPositionToDate(gridPos)
        updateEzSchedule({
            dest_unit_ready_date: clickedDate.toISO(),
        })
    }

    const handleChooseDtUnitReadyDate = (clickX: number) => {
        if (ezSchedule === null || grid.gridRef.current === null) return

        const gridPos = grid.getClickedGridPosition(clickX)
        const clickedDate = grid.convertGridPositionToDate(gridPos)
        updateEzSchedule({
            dt_unit_ready_date: clickedDate.toISO(),
        })
    }

    let cumulativeRowCount = 0

    const unitCount = templateUnits.reduce((acc, unit) => {
        if (unit.is_included) {
            return acc + 1
        }
        return acc
    }, 0)

    const serviceAreaCount = selectedPhase?.total_service_areas ?? 0

    const useOrderControl = ezSchedule.use_order_control ?? false

    const gridContentWidth = grid.gridPositionInfo.numDisplayDays * DAY_CELL_W

    return (
        <div className={classes.root}>
            <div
                style={{
                    display: 'flex',
                    width: 'max-content',
                    minWidth: '100%',
                }}
            >
                {/* Left Column - pinned to left edge during horizontal scroll */}
                <div
                    style={{
                        position: 'sticky',
                        left: 0,
                        zIndex: theme.zIndex.modal + 2,
                        backgroundColor: theme.palette.background.paper,
                        width: 350,
                        minWidth: 350,
                        flexShrink: 0,
                    }}
                >
                    <Grid container style={{ alignContent: 'flex-start' }}>
                        <ServiceDetailHeader
                            unitCount={unitCount}
                            serviceAreaCount={serviceAreaCount}
                        />

                        {templateServices.map((ts, index) => {
                            return (
                                <TemplateServiceDetailRow
                                    key={`TEMPLATE-SERVICE-${ts.id}`}
                                    {...props}
                                    templateService={ts}
                                    projectDuration={
                                        grid.gridPositionInfo.numProjectDays
                                    }
                                    updateTemplateServiceVendor={
                                        updateTemplateServiceVendor
                                    }
                                    infrastructure={infrastructure}
                                    useOrderControl={useOrderControl}
                                    isFirstService={index === 0}
                                    workOrderCount={
                                        workOrderCountByService[index] ?? 0
                                    }
                                />
                            )
                        })}
                    </Grid>
                </div>

                {/* Spacer */}
                <div style={{ width: 8, flexShrink: 0 }} />

                {/* Right Column - chart area */}
                <div
                    ref={grid.gridRef}
                    style={{
                        position: 'relative',
                        width: gridContentWidth,
                        minWidth: gridContentWidth,
                        flexShrink: 0,
                        zIndex: 1,
                    }}
                >
                    <CalendarGrid
                        {...props}
                        calendarGridState={grid}
                        startDate={startDate}
                        endDate={endDate}
                        handleChooseStartTransferDate={
                            handleChooseStartTransferDate
                        }
                        handleChooseEndTransferDate={
                            handleChooseEndTransferDate
                        }
                        handleChooseDestUnitReadyDate={
                            handleChooseDestUnitReadyDate
                        }
                        handleChooseDtUnitReadyDate={
                            handleChooseDtUnitReadyDate
                        }
                        selectedPhaseId={selectedPhaseId}
                    />

                    {templateServices.map((ts, index) => {
                        const rowCountForThisService =
                            1 + ts.template_service_vendors.length
                        const prevRowCount = cumulativeRowCount
                        cumulativeRowCount += rowCountForThisService

                        const showDisabledOrderControl =
                            useOrderControl && index > 0

                        if (showDisabledOrderControl) {
                            const topOffset =
                                HEADER_H + (prevRowCount + 1) * VENDOR_BAR_H
                            const containerHeight = Math.max(
                                ts.template_service_vendors.length *
                                    VENDOR_BAR_H,
                                VENDOR_BAR_H,
                            )
                            return (
                                <Container
                                    key={`CHART-SERVICE-BAR${ts.id}`}
                                    className={
                                        classes.orderControlDisabledChartRow
                                    }
                                    style={{
                                        position: 'absolute',
                                        top: topOffset,
                                        left: 0,
                                        right: 0,
                                        height: containerHeight,
                                        minHeight: VENDOR_BAR_H,
                                    }}
                                >
                                    <Typography variant="body2">
                                        {workOrderCountByService[index] ?? 0}{' '}
                                        work order
                                        {(workOrderCountByService[index] ??
                                            0) !== 1
                                            ? 's'
                                            : ''}{' '}
                                        — Dates will be assigned after the
                                        previous service is complete.
                                    </Typography>
                                </Container>
                            )
                        }

                        return (
                            <TemplateServiceChartRow
                                key={`CHART-SERVICE-BAR${ts.id}`}
                                {...props}
                                gridState={grid}
                                templateService={ts}
                                projectStartDate={startDate}
                                projectEndDate={endDate}
                                prevRowCount={prevRowCount}
                                timezoneState={timezoneState}
                                theme={theme}
                                ezSchedule={ezSchedule}
                                templateWorkorders={templateWorkorders}
                                workordersByVendorDate={workordersByVendorDate}
                                updateTemplateServiceVendor={
                                    updateTemplateServiceVendor
                                }
                                selectedPhaseId={selectedPhaseId}
                            />
                        )
                    })}
                </div>
            </div>
        </div>
    )
}
